import Link from 'next/link';

import cx from 'classnames';
import GGWordmark from 'themes/canvas/assets/GG_wordmark_blk.svg';

import { useAppContext } from 'hooks/context/useAppContext';
import { useNavigation } from 'hooks/navigation/useNavigation';
import { useFooter } from 'hooks/page/footer/useFooter';

import { CanvasAnimatedTile } from '../CanvasAnimatedTile';
import { CanvasBusinessHours } from '../CanvasBusinessHours/CanvasBusinessHours';
import useDynamicFontSize from '../utils';

import s from './CanvasFooter.module.css';

const CanvasFooter = () => {
  const {
    address,
    termsOfServiceUrl,
    privacyPolicyUrl,
    addressLink,
    showAddress,
  } = useFooter();
  const { publicUser } = useAppContext();
  const { showPortfolio, showTeam, socialMediaUrls } = useNavigation();
  const allNull = Object.values(socialMediaUrls).every(
    (value) => value === null,
  );

  const { containerRef, fontSize } = useDynamicFontSize(
    publicUser.business_name,
  );

  const businessNameStyle: React.CSSProperties = {
    fontSize: fontSize ? `${fontSize}px` : 'inherit',
  };

  interface Urls {
    instagram_url?: string;
    facebook_url?: string;
    yelp_url?: string;
    twitter_url?: string;
    website_url?: string;
    pinterest_url?: string;
  }

  type TUrlsKeys = keyof Urls;

  const links: {
    name: TUrlsKeys;
    prefix: string;
    suffix: string;
  }[] = [
    {
      name: 'facebook_url',
      prefix: 'https://www.facebook.com/',
      suffix: 'Facebook',
    },
    {
      name: 'twitter_url',
      prefix: 'https://twitter.com/',
      suffix: 'Twitter',
    },
    {
      name: 'instagram_url',
      prefix: 'https://www.instagram.com/',
      suffix: 'Instagram',
    },
    {
      name: 'pinterest_url',
      prefix: 'https://www.pinterest.com/',
      suffix: 'Pinterest',
    },
    {
      name: 'yelp_url',
      prefix: 'https://www.yelp.com/',
      suffix: 'Yelp',
    },
    { name: 'website_url', prefix: '', suffix: 'Official Site' },
  ];

  return (
    <footer className={s.footer}>
      <div className={s.content}>
        {showAddress ? (
          <a
            href={addressLink}
            className={s.address}
            target="_blank"
            rel="noopener noreferrer"
          >
            {address}
          </a>
        ) : (
          <div className={s.address} />
        )}
        <div className={s.internalLinks}>
          <Link href="/about">About Us</Link>
          <Link href="/services">Services</Link>
          {showTeam && <Link href="/team">Team</Link>}
          {showPortfolio && <Link href="/portfolio">Portfolio</Link>}
        </div>
        <div
          className={cx(s.socialLinks, {
            [s.socialLinksGone]: allNull,
          })}
        >
          {socialMediaUrls && !allNull && (
            <>
              <p>GET IN TOUCH</p>
              {links.map(({ name, prefix, suffix }) => {
                const url = socialMediaUrls[name];

                return (
                  url && (
                    <a
                      key={name}
                      href={url.startsWith('http') ? url : `${prefix}${url}`}
                      target="_blank"
                      rel="noreferrer"
                      title={`Link to ${suffix} profile`}
                    >
                      {suffix}
                    </a>
                  )
                );
              })}
            </>
          )}
        </div>
        <CanvasBusinessHours />
      </div>
      <CanvasAnimatedTile>
        <div ref={containerRef}>
          <h3 style={businessNameStyle} className={s.businessName}>
            {publicUser?.business_name}
          </h3>
        </div>

        <div className={s.termsAndLogo}>
          <div className={s.links}>
            <a
              href={termsOfServiceUrl}
              className={s.linksItem}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms
            </a>
            <a
              href={privacyPolicyUrl}
              className={s.linksItem}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </div>
          <div className={s.poweredByContainer}>
            <a
              href="https://glossgenius.com"
              className={s.logo}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className={s.poweredByLabel}>POWERED BY</span>
              <GGWordmark className={s.wordMark} />
            </a>
          </div>
        </div>
      </CanvasAnimatedTile>
    </footer>
  );
};

export { CanvasFooter };
